/**
 * NewMapDialog: Popup for creating a new beatmap from an audio file.
 * User uploads audio, enters song metadata, and creates empty map.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { createBeatmap } from '../../api/client';
import { API_BASE } from '../../api/apiBase';
import { color, ui, z } from '../ui/theme';
import AutoBpmDialog from '../ui/AutoBpmDialog';
import { useAutoBpm } from '../ui/useAutoBpm';
import { IcMetro } from '../ui/icons';

// BPM/offset detection here runs on the picked File directly (no beatmap exists
// yet — this dialog is what CREATES one) — same backend endpoint the loaded-map
// detectors (srAudioDetect.ts etc.) use, just fed the raw File instead of a
// fetched beatmap's audio URL.
async function detectBpmFromFile(file: File): Promise<number> {
  const form = new FormData();
  form.append('audio', file, file.name);
  const res = await fetch(`${API_BASE}/api/audio/analyze`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`Analysis failed (${res.status})`);
  const j = await res.json();
  const bpm = Number(j?.bpm);
  if (!Number.isFinite(bpm) || bpm <= 0) throw new Error('No BPM detected');
  return Math.round(bpm * 100) / 100;
}

async function detectOffsetFromFile(file: File, bpm: number | null): Promise<number> {
  const form = new FormData();
  form.append('audio', file, file.name);
  if (bpm) form.append('bpm', String(bpm));
  const res = await fetch(`${API_BASE}/api/audio/analyze`, { method: 'POST', body: form });
  if (res.ok) {
    const j = await res.json();
    const off = Number(j?.offset_seconds);
    if (Number.isFinite(off) && off >= 0) return Math.round(off * 1000);   // seconds → ms
  }
  // Client-side RMS onset scan fallback (same algorithm as srAudioDetect.ts).
  const bytes = await file.arrayBuffer();
  const Ctx: typeof AudioContext =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  let buffer: AudioBuffer;
  try { buffer = await ctx.decodeAudioData(bytes.slice(0)); }
  finally { ctx.close().catch(() => { /* ignore */ }); }

  const data = buffer.getChannelData(0);
  const sr = buffer.sampleRate;
  const win = Math.max(1, Math.floor(sr * 0.010));
  const nWin = Math.floor(data.length / win);
  if (nWin === 0) return 0;
  const rms = new Float32Array(nWin);
  let peak = 0;
  for (let w = 0; w < nWin; w++) {
    const base = w * win;
    let sum = 0;
    for (let i = 0; i < win; i++) { const s = data[base + i]; sum += s * s; }
    const r = Math.sqrt(sum / win);
    rms[w] = r;
    if (r > peak) peak = r;
  }
  if (peak <= 0) return 0;
  const threshold = Math.max(peak * 0.08, 0.008);
  const sustain = 3;
  for (let w = 0; w < nWin; w++) {
    if (rms[w] < threshold) continue;
    let ok = true;
    for (let k = 1; k < sustain && w + k < nWin; k++) {
      if (rms[w + k] < threshold * 0.6) { ok = false; break; }
    }
    if (!ok) continue;
    const t = (w * win) / sr - 0.005;
    return Math.max(0, Math.round(t * 1000));   // ms
  }
  return 0;
}

export default function NewMapDialog() {
  const open = useEditorStore(s => s.newMapOpen);
  const setOpen = useEditorStore(s => s.setNewMapOpen);
  const loadBeatmap = useEditorStore(s => s.loadBeatmap);

  const [name, setName] = useState('');
  const [author, setAuthor] = useState('');
  const [mapper, setMapper] = useState('');
  const [bpm, setBpm] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoOffsetBusy, setAutoOffsetBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Auto BPM drives the shared metronome popup (detect → done), same as every
  // other editor's Controls panel — just reading the not-yet-uploaded File.
  const autoBpm = useAutoBpm(
    useCallback(() => {
      if (!audioFile) throw new Error('Select an audio file first');
      return detectBpmFromFile(audioFile);
    }, [audioFile]),
    useCallback((b: number) => setBpm(b), []),
  );

  const handleAutoOffset = useCallback(async () => {
    if (!audioFile) { setError('Select an audio file first'); return; }
    setAutoOffsetBusy(true);
    try { const off = await detectOffsetFromFile(audioFile, bpm); setOffset(off); }
    catch (e) { setError((e as Error).message || 'Offset detection failed'); }
    finally { setAutoOffsetBusy(false); }
  }, [audioFile, bpm]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // Clean up audio preview URL on unmount
  useEffect(() => {
    return () => {
      if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
    };
  }, [audioPreviewUrl]);

  const handleAudioSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setAudioFile(file);
    setError(null);

    // Auto-fill name from filename
    if (!name) {
      const baseName = file.name.replace(/\.(ogg|mp3|wav|flac|m4a)$/i, '');
      setName(baseName);
    }

    // Create preview URL
    if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
    setAudioPreviewUrl(URL.createObjectURL(file));
  }, [name, audioPreviewUrl]);

  const handleCreate = useCallback(async () => {
    if (!audioFile) {
      setError('Please select an audio file');
      return;
    }
    if (!name.trim()) {
      setError('Please enter a song name');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const beatmap = await createBeatmap(
        audioFile,
        name.trim(),
        author.trim() || 'Unknown',
        mapper.trim(),
        bpm ?? 120.0,
        offset,
      );

      loadBeatmap(beatmap);
      setOpen(false);

      // Reset form
      setName('');
      setAuthor('');
      setMapper('');
      setBpm(null);
      setOffset(0);
      setAudioFile(null);
      if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
      setAudioPreviewUrl(null);

    } catch (e: any) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  }, [audioFile, name, author, mapper, bpm, offset, loadBeatmap, setOpen, audioPreviewUrl]);

  if (!open) return null;

  return (
    <div style={overlayStyle} onClick={() => setOpen(false)}>
      <div style={dialogStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <span style={{ fontWeight: 600, fontSize: 14, color: color.ink }}>
            {'\u2728'} New Beatmap
          </span>
          <button onClick={() => setOpen(false)} style={closeBtnStyle}>{'\u2715'}</button>
        </div>

        <div style={bodyStyle}>
          {/* Audio upload */}
          <SectionLabel text="Audio File" />
          <input
            ref={fileRef}
            type="file"
            accept=".ogg,.mp3,.wav,.flac,.m4a"
            onChange={e => handleAudioSelect(e.target.files)}
            style={{ display: 'none' }}
          />

          <div
            onClick={() => fileRef.current?.click()}
            style={{
              ...dropZoneStyle,
              borderColor: audioFile ? 'rgba(52, 211, 153, 0.4)' : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.2)',
              background: audioFile
                ? 'linear-gradient(135deg, rgba(52, 211, 153, 0.08), rgba(52, 211, 153, 0.03))'
                : 'rgba(15, 15, 30, 0.4)',
            }}
          >
            {audioFile ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#34D399', fontSize: 12, fontWeight: 600 }}>
                  {'\u266B'} {audioFile.name}
                </div>
                <div style={{ color: color.inkFaint, fontSize: 10, marginTop: 2 }}>
                  {(audioFile.size / 1024 / 1024).toFixed(1)} MB — Click to change
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>{'\uD83C\uDFB5'}</div>
                <div style={{ color: color.inkDim, fontSize: 11 }}>
                  Click to select audio file
                </div>
                <div style={{ color: '#4A4565', fontSize: 9, marginTop: 2 }}>
                  OGG, MP3, WAV, FLAC, M4A
                </div>
              </div>
            )}
          </div>

          {/* Audio preview */}
          {audioPreviewUrl && (
            <audio
              src={audioPreviewUrl}
              controls
              style={{ width: '100%', height: 28, marginTop: 4, borderRadius: 6 }}
            />
          )}

          {/* Song Info */}
          <SectionLabel text="Song Info" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <LabeledInput label="Song Name" value={name} onChange={setName} placeholder="Song title" />
            <LabeledInput label="Author" value={author} onChange={setAuthor} placeholder="Artist name" />
            <LabeledInput label="Mapper" value={mapper} onChange={setMapper} placeholder="Your name" />
          </div>

          {/* BPM + Offset */}
          <SectionLabel text="Timing" />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={fieldLabel}>BPM</div>
              <div style={{ display: 'flex', gap: 4 }}>
                <input
                  type="number"
                  min={1}
                  max={999}
                  step={0.1}
                  placeholder="Auto-detect"
                  value={bpm ?? ''}
                  onChange={e => setBpm(e.target.value ? parseFloat(e.target.value) : null)}
                  style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                />
                <button
                  type="button"
                  title="Auto-detect BPM from the audio"
                  disabled={!audioFile || autoBpm.busy}
                  onClick={autoBpm.run}
                  style={{ ...autoBtnStyle, opacity: !audioFile || autoBpm.busy ? 0.5 : 1 }}
                >
                  <IcMetro size={13} />
                </button>
              </div>
              <div style={{ color: '#4A4565', fontSize: 8, marginTop: 2 }}>
                Leave blank to auto-detect
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={fieldLabel}>Offset (ms)</div>
              <div style={{ display: 'flex', gap: 4 }}>
                <input
                  type="number"
                  step={1}
                  value={offset}
                  onChange={e => setOffset(parseFloat(e.target.value) || 0)}
                  style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                />
                <button
                  type="button"
                  title="Auto-detect offset from the audio's first beat"
                  disabled={!audioFile || autoOffsetBusy}
                  onClick={handleAutoOffset}
                  style={{ ...autoBtnStyle, opacity: !audioFile || autoOffsetBusy ? 0.5 : 1 }}
                >
                  {autoOffsetBusy ? '⏳' : '⏱'}
                </button>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={errorStyle}>{error}</div>
          )}

          {/* Create button */}
          <button
            onClick={handleCreate}
            disabled={creating || !audioFile}
            style={{
              ...createBtnStyle,
              opacity: creating || !audioFile ? 0.5 : 1,
              cursor: creating || !audioFile ? 'not-allowed' : 'pointer',
            }}
          >
            {creating ? '\u23F3 Creating...' : '\u2728 Create Beatmap'}
          </button>
        </div>
      </div>

      <AutoBpmDialog
        stage={autoBpm.stage}
        detected={autoBpm.detected}
        final={autoBpm.detected}
        onChoose={autoBpm.choose}
        onClose={autoBpm.dismiss}
      />
    </div>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{
      fontSize: 10, color: color.inkFaint, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: 1.5,
      marginTop: 12, marginBottom: 4,
    }}>
      {text}
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div>
      <div style={fieldLabel}>{label}</div>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = { ...ui.overlay };

const dialogStyle: React.CSSProperties = { ...ui.dialog, width: '90%', maxWidth: 400, maxHeight: '85vh', overflowY: 'auto' };

const headerStyle: React.CSSProperties = { ...ui.dialogHeader, padding: '14px 20px' };

const closeBtnStyle: React.CSSProperties = { ...ui.closeBtn };

const bodyStyle: React.CSSProperties = {
  padding: '12px 20px 20px', overflowY: 'auto', flex: 1,
  display: 'flex', flexDirection: 'column',
};

const dropZoneStyle: React.CSSProperties = {
  border: '2px dashed oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.2)',
  borderRadius: 12, padding: '20px 16px',
  cursor: 'pointer', transition: 'all 0.2s ease',
};

const inputStyle: React.CSSProperties = { ...ui.input, width: '100%', boxSizing: 'border-box' };

// Compact square "auto-detect" button sitting right next to the BPM/Offset inputs.
const autoBtnStyle: React.CSSProperties = {
  flexShrink: 0, width: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 8, cursor: 'pointer', fontSize: 13,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: 'var(--accent)',
};

const fieldLabel: React.CSSProperties = {
  fontSize: 9, color: color.inkFaint, fontWeight: 500, marginBottom: 2,
  textTransform: 'uppercase', letterSpacing: 0.5,
};

const errorStyle: React.CSSProperties = {
  color: '#F87171', fontSize: 11, padding: '6px 10px',
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.15)',
  borderRadius: 8, marginTop: 8,
};

const createBtnStyle: React.CSSProperties = {
  ...ui.btn,
  width: '100%', fontSize: 13, letterSpacing: 0.5, marginTop: 14,
  boxShadow: '0 2px 16px oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.3)',
  transition: 'all 0.2s ease',
};
